import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ease } from '@/lib/motion';

interface BootSplashProps {
  /** Vira true quando o auth (/auth/me) já resolveu — só fecha quando estiver pronto. */
  gateReady: boolean;
  /** Tempo mínimo de exibição (ms) — garante que a revelação da logo termine. */
  minDuration?: number;
  /** Trava de segurança: fecha de qualquer forma após esse tempo. */
  maxDuration?: number;
  onDone: () => void;
}

/**
 * Boot: fundo branco, logo empilhada UNDER / LINE_ revelada por um wipe da esquerda
 * pra direita; o underscore vermelho desenha por último e fica piscando — o blink é o
 * próprio indicador de "carregando/vivo" (mesma assinatura do rawlabz). Responsivo por
 * largura (clamp), sem barra de progresso. Fecha com fade + leve lift quando o auth
 * resolve (ou no maxDuration).
 */
export function BootSplash({
  gateReady,
  minDuration = 1500,
  maxDuration = 4500,
  onDone,
}: BootSplashProps) {
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const reduce = useReducedMotion();

  // Refs p/ o loop de rAF ler valores atuais sem recriar o efeito.
  const gateReadyRef = useRef(gateReady);
  gateReadyRef.current = gateReady;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (reduce) {
      const t = setTimeout(() => onDoneRef.current(), 160);
      return () => clearTimeout(t);
    }

    let raf = 0;
    let outTimer = 0;
    const start = performance.now();

    // Espera fontes p/ evitar FOUT na legenda mono (não trava além do maxDuration).
    let fontsDone = false;
    const fontsReady =
      typeof document !== 'undefined' && document.fonts ? document.fonts.ready : Promise.resolve();
    void fontsReady.then(
      () => {
        fontsDone = true;
      },
      () => {
        fontsDone = true;
      },
    );

    const tick = (now: number) => {
      const elapsed = now - start;
      const ready =
        elapsed >= minDuration && ((fontsDone && gateReadyRef.current) || elapsed >= maxDuration);
      if (ready) {
        setPhase('out');
        outTimer = window.setTimeout(() => onDoneRef.current(), 460);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(outTimer);
    };
  }, [minDuration, maxDuration, reduce]);

  const out = phase === 'out';

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-background"
      initial={{ opacity: 1 }}
      animate={{ opacity: out ? 0 : 1 }}
      transition={{ duration: out ? 0.44 : 0.2, ease }}
      style={{ pointerEvents: out ? 'none' : 'auto' }}
      role="status"
      aria-live="polite"
      aria-label="Carregando"
    >
      <motion.div
        className="flex w-full flex-col items-center gap-6 px-8"
        animate={{ y: out ? -14 : 0 }}
        transition={{ duration: 0.4, ease }}
      >
        {/* Logo empilhada responsiva: largura fluida limitada — nunca estoura no mobile. */}
        <div className="w-full" style={{ maxWidth: 'min(76vw, 340px)' }}>
          <StackedWordmark reduce={!!reduce} />
        </div>

        {/* Legenda mono — papel/utilitária (estilo rawlabz). */}
        <motion.span
          className="font-mono text-[10px] uppercase tracking-[0.34em] text-muted-foreground"
          initial={reduce ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: out ? 0 : 0.85, y: 0 }}
          transition={{ duration: 0.5, ease, delay: reduce ? 0 : 0.9 }}
        >
          Programação &amp; MKT
        </motion.span>
      </motion.div>
    </motion.div>
  );
}

/**
 * Logo empilhada (UNDER / LINE_) com revelação: wipe da esquerda → direita nas letras
 * e o underscore vermelho desenhando (scaleX) e depois piscando. Em reduced-motion,
 * renderiza estática.
 */
function StackedWordmark({ reduce }: { reduce: boolean }) {
  const letters = (
    <g fill="var(--foreground)" clipPath={reduce ? undefined : 'url(#boot-reveal)'}>
      {/* UNDER (linha de cima) */}
      <path d="M6.38,36.17c3.56,2.27,8.43,3.62,14.61,4.06,2.76.19,5.49.28,8.2.28,6.7,0,12.06-.51,16.09-1.53,8.74-2.18,13.11-7.41,13.11-15.72V0h-15.86v21.56c0,1.46-.28,2.65-.85,3.58s-1.32,1.65-2.27,2.17c-.95.52-2,.89-3.16,1.11-1.16.22-2.36.35-3.59.4-1.23.05-2.38.07-3.46.07-3.08,0-5.44-.15-7.06-.45-4.18-.81-6.27-3.11-6.27-6.89V0H0v23.26c0,5.86,2.13,10.17,6.38,12.91Z" />
      <polygon points="79.85 16.03 102.75 39.15 122.38 39.15 122.38 0 106.52 0 106.52 23.09 83.6 0 63.99 0 63.99 39.15 79.85 39.15 79.85 16.03" />
      <path d="M167.99,39.15c2.89,0,5.73-.47,8.51-1.4,2.78-.94,5.04-2.2,6.78-3.79,4.35-4.03,6.53-8.82,6.53-14.38s-2.18-10.36-6.53-14.38c-1.74-1.59-4-2.85-6.78-3.79-2.78-.94-5.62-1.4-8.51-1.4h-38.16v39.15l38.1.14.06-.14ZM145.69,27.18v-15.21h21.02c1.74,0,3.27.47,4.6,1.42,1.74,1.27,2.61,3.33,2.61,6.18s-.87,4.89-2.61,6.16c-1.32.96-2.86,1.45-4.6,1.45h-21.02Z" />
      <polygon points="247.95 27.66 208.03 27.66 208.03 23.77 245.34 23.77 245.34 15.15 208.03 15.15 208.03 11.46 247.5 11.46 247.5 0 192.18 0 192.18 39.15 247.95 39.15 247.95 27.66" />
      <path d="M310.51,36.27c-.5-1.39-.81-3.8-.92-7.22-.11-3.42-2.01-5.87-5.7-7.35,4.26-2.02,6.38-5.3,6.38-9.82,0-1.08-.15-2.27-.44-3.59-.29-1.31-.99-2.62-2.09-3.93-2.48-2.91-6.96-4.37-13.45-4.37h-42.19v39.15h15.86v-10.53h17.96c2.1,0,3.71.17,4.82.51s1.79,1.18,2.01,2.52c.15.93.23,1.88.23,2.85s.07,1.88.21,2.71c.14.83.49,1.48,1.04,1.93h18.21c-.79-.53-1.44-1.49-1.94-2.88ZM293.86,15.48c-.29.44-.84.83-1.65,1.16-.8.33-1.94.5-3.42.5h-20.82v-5.67h20.82c2.44,0,4.05.35,4.82,1.05.45.42.68,1.01.68,1.79,0,.34-.15.73-.44,1.18Z" />
      {/* LINE (linha de baixo) */}
      <polygon points="53.36 75.59 15.86 75.59 15.86 48.41 0 48.41 0 87.56 53.36 87.56 53.36 75.59" />
      <rect x="56.67" y="48.41" width="15.86" height="39.15" />
      <polygon points="79.98 48.41 79.98 87.56 95.84 87.56 95.84 64.44 118.74 87.56 138.37 87.56 138.37 48.41 122.51 48.41 122.51 71.5 99.58 48.41 79.98 48.41" />
      <polygon points="161.68 72.18 198.98 72.18 198.98 63.56 161.68 63.56 161.68 59.87 201.14 59.87 201.14 48.41 145.82 48.41 145.82 87.56 201.59 87.56 201.59 76.07 161.68 76.07 161.68 72.18" />
    </g>
  );

  return (
    <svg
      viewBox="0 0 312.45 87.56"
      className="block h-auto w-full"
      role="img"
      aria-label="Underline"
      preserveAspectRatio="xMidYMid meet"
    >
      {!reduce && (
        <defs>
          <clipPath id="boot-reveal">
            <motion.rect
              x="0"
              y="0"
              height="87.56"
              initial={{ width: 0 }}
              animate={{ width: 312.45 }}
              transition={{ duration: 0.8, ease, delay: 0.12 }}
            />
          </clipPath>
        </defs>
      )}

      {letters}

      {reduce ? (
        <rect x="209.2" y="76.91" width="75.27" height="10.65" fill="var(--primary)" />
      ) : (
        <motion.rect
          x="209.2"
          y="76.91"
          width="75.27"
          height="10.65"
          fill="var(--primary)"
          style={{ transformBox: 'fill-box', transformOrigin: 'left center' }}
          initial={{ scaleX: 0, opacity: 1 }}
          animate={{ scaleX: 1, opacity: [1, 0.2, 1] }}
          transition={{
            scaleX: { duration: 0.42, ease, delay: 0.9 },
            opacity: { duration: 1.4, ease: 'easeInOut', repeat: Infinity, delay: 1.35 },
          }}
        />
      )}
    </svg>
  );
}
